import { useState, useMemo } from 'react';
import FishDetailOverlay from './FishDetailOverlay';
import { effectFilters, rarityGlows } from '../constants/effects';
import { FISH_LIBRARY } from '../constants/fishLibrary';
import StarRating from './StarRating';
import { coinIcon } from '../constants/assets';

// ---------------------------------------------------------------------------
// Sorting helpers — defined at module level, never re-created on re-render
// ---------------------------------------------------------------------------

const RARITY_ORDER = {
  common: 0, uncommon: 1, rare: 2, special: 3, legendary: 4, mythical: 5,
};

// Pre-build a lookup of fish id → library index for O(1) sort access
const FISH_ORDER = Object.fromEntries(
  FISH_LIBRARY.map((fish, index) => [fish.id, index])
);

const SORT_OPTIONS = [
  { value: 'date',   label: 'DATE CAUGHT' },
  { value: 'fish',   label: 'FISH TYPE'   },
  { value: 'rarity', label: 'RARITY'      },
];

const sortInventory = (inventory, sortMode) => {
  const copy = [...inventory];
  switch (sortMode) {
    case 'fish':
      return copy.sort((a, b) =>
        (FISH_ORDER[a.id] ?? 999) - (FISH_ORDER[b.id] ?? 999)
      );
    case 'rarity':
      return copy.sort((a, b) =>
        (RARITY_ORDER[a.rarity.toLowerCase()] ?? 0) -
        (RARITY_ORDER[b.rarity.toLowerCase()] ?? 0)
      );
    case 'date':
    default:
      return copy.sort((a, b) =>
        new Date(a.caughtAt) - new Date(b.caughtAt)
      );
  }
};

// ---------------------------------------------------------------------------

export default function InventoryView({
  inventory,
  gold,
  selectedFish,
  onSelectFish,
  onSell,
  onBack,
}) {
  const [selectedIndex, setSelectedIndex]   = useState(null);
  const [sortMode,      setSortMode]        = useState('date');
  const [sortMenuOpen,  setSortMenuOpen]    = useState(false);

  // Sorted inventory — recomputed only when inventory or sortMode changes
  const sortedInventory = useMemo(
    () => sortInventory(inventory, sortMode),
    [inventory, sortMode]
  );

  const activeFish = selectedIndex !== null ? sortedInventory[selectedIndex] : null;

  const selectByIndex = (index) => {
    setSelectedIndex(index);
    onSelectFish(sortedInventory[index]);
  };

  const handleClose = () => {
    setSelectedIndex(null);
    onSelectFish(null);
  };

  const handleSwipeLeft = () => {
    if (selectedIndex === null) return;
    selectByIndex((selectedIndex + 1) % sortedInventory.length);
  };

  const handleSwipeRight = () => {
    if (selectedIndex === null) return;
    selectByIndex((selectedIndex - 1 + sortedInventory.length) % sortedInventory.length);
  };

  const handleSortSelect = (value) => {
    setSortMode(value);
    setSortMenuOpen(false);
    // Reset selection so the index doesn't point to the wrong fish after re-sort
    setSelectedIndex(null);
    onSelectFish(null);
  };

  const activeFishWithMeta = activeFish
    ? { ...activeFish, _index: selectedIndex, _total: sortedInventory.length }
    : null;

  const currentLabel = SORT_OPTIONS.find(o => o.value === sortMode)?.label;

  return (
    <div style={{
      display: 'flex', flexDirection: 'column', alignItems: 'center',
      height: '100vh', width: '100%', backgroundColor: '#05070a',
      color: 'white', padding: '20px', boxSizing: 'border-box', overflowY: 'auto',
    }}>

      <h2 style={{ color: '#f1c40f', marginBottom: '20px', letterSpacing: '2px' }}>INVENTORY</h2>

      {/* Gold Balance */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: '8px', backgroundColor: '#1c2128',
        padding: '8px 16px', borderRadius: '20px', border: '1px solid #30363d',
        marginBottom: '16px', boxShadow: '0 0 10px rgba(241, 196, 15, 0.1)',
      }}>
        <img src={coinIcon} alt="gold" style={{ width: '18px', height: '18px', imageRendering: 'pixelated' }} />
        <span style={{ color: '#ffffff', fontWeight: 'bold', fontSize: '16px', letterSpacing: '1px' }}>
          {gold.toLocaleString()}
        </span>
      </div>

      {/* Sort Dropdown */}
      <div style={{ position: 'relative', marginBottom: '20px' }}>
        <button
          onClick={() => setSortMenuOpen(prev => !prev)}
          style={{
            backgroundColor: '#1c2128', color: '#8b949e',
            border: '1px solid #30363d', borderRadius: '6px',
            padding: '8px 14px', fontSize: '8px', fontFamily: 'inherit',
            cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px',
            letterSpacing: '1px',
          }}
        >
          SORT: {currentLabel}
          <span style={{ fontSize: '10px' }}>{sortMenuOpen ? '▲' : '▼'}</span>
        </button>

        {sortMenuOpen && (
          <div style={{
            position: 'absolute', top: '110%', left: 0,
            backgroundColor: '#1c2128', border: '1px solid #30363d',
            borderRadius: '6px', overflow: 'hidden', zIndex: 50,
            minWidth: '100%',
          }}>
            {SORT_OPTIONS.map(option => (
              <button
                key={option.value}
                onClick={() => handleSortSelect(option.value)}
                style={{
                  display: 'block', width: '100%', padding: '10px 14px',
                  backgroundColor: sortMode === option.value ? '#30363d' : 'transparent',
                  color: sortMode === option.value ? '#ffffff' : '#8b949e',
                  border: 'none', textAlign: 'left', fontFamily: 'inherit',
                  fontSize: '8px', cursor: 'pointer', letterSpacing: '1px',
                  whiteSpace: 'nowrap',
                }}
                onMouseEnter={e => { e.target.style.backgroundColor = '#30363d'; }}
                onMouseLeave={e => {
                  e.target.style.backgroundColor =
                    sortMode === option.value ? '#30363d' : 'transparent';
                }}
              >
                {option.label}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Detail overlay */}
      {activeFishWithMeta && (
        <FishDetailOverlay
          fish={activeFishWithMeta}
          onKeep={handleClose}
          onSell={onSell}
          onSwipeLeft={sortedInventory.length > 1 ? handleSwipeLeft : undefined}
          onSwipeRight={sortedInventory.length > 1 ? handleSwipeRight : undefined}
        />
      )}

      {inventory.length === 0 ? (
        <div style={{ textAlign: 'center', marginTop: '50px', color: '#8b949e' }}>
          <p>Your inventory is empty.</p>
          <p>Go catch some fish!</p>
        </div>
      ) : (
        <div style={{
          display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(70px, 1fr))',
          gap: '12px', width: '100%', maxWidth: '500px',
        }}>
          {sortedInventory.map((fish, index) => (
            <div
              key={`${fish.instanceId ?? fish.caughtAt}-${index}`}
              onClick={() => selectByIndex(index)}
              style={{
                cursor: 'pointer', backgroundColor: '#161b22',
                border: `2px solid ${rarityGlows[fish.rarity.toLowerCase()] || '#30363d'}`,
                borderRadius: '8px', padding: '8px',
                display: 'flex', flexDirection: 'column', alignItems: 'center',
                position: 'relative',
                boxShadow: `0 0 10px ${rarityGlows[fish.rarity.toLowerCase()] || 'transparent'}40`,
              }}
            >
              <img
                src={`assets/fish/${fish.id}.png`}
                alt={fish.name}
                className={fish.effect === 'prismatic' ? 'effect-prismatic' : ''}
                style={{
                  width: '100%', height: '40px', objectFit: 'contain',
                  imageRendering: 'pixelated',
                  filter: effectFilters[fish.effect.toLowerCase()] || 'none',
                }}
                onError={(e) => { e.target.src = 'https://via.placeholder.com/40?text=?'; }}
              />
              <span style={{
                fontSize: '9px', marginTop: '6px', textAlign: 'center',
                whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                width: '100%', textTransform: 'uppercase',
              }}>
                {fish.name}
              </span>
              <span style={{ position: 'absolute', top: '1px', right: '4px' }}>
                <StarRating count={fish.stars} size={8} />
              </span>
            </div>
          ))}
        </div>
      )}

      <button
        onClick={onBack}
        className="menu-btn"
        style={{
          marginTop: '30px', padding: '12px 24px', backgroundColor: '#21262d',
          color: '#c9d1d9', border: '1px solid #30363d', borderRadius: '6px',
          cursor: 'pointer', fontFamily: 'inherit', fontSize: '12px',
        }}
      >
        BACK TO MENU
      </button>

    </div>
  );
}